import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  collection,
  query as fsQuery,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseWeb";
import { ensureFirebaseLoginWeb } from "../firebase/ensureFirebaseLoginWeb";
import { AuthContextWeb } from "../context/AuthContextWeb";

function fmtTime(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const diffMs = now - d;
  if (diffMs < 7 * 24 * 60 * 60 * 1000) {
    return d.toLocaleDateString([], { weekday: "short" });
  }
  return d.toLocaleDateString();
}

// ── legacy demo data removed ──

export default function InboxPage() {
  const { user, token } = useContext(AuthContextWeb);
  const [searchParams] = useSearchParams();

  const uid = String(user?.userId || "");
  const role = (user?.role || "").toUpperCase();
  const isOwner = ["OWNER", "ADMIN", "SUPER_ADMIN"].includes(role);

  /* Firebase readiness */
  const [fbReady, setFbReady] = useState(false);
  const [fbError, setFbError] = useState("");

  /* Conversation list */
  const [conversations, setConversations] = useState([]);
  const [convoLoading, setConvoLoading] = useState(true);
  const [convoError, setConvoError] = useState("");

  /* Selected conversation & messages */
  const [selectedId, setSelectedId] = useState(searchParams.get("c") || null);
  const [messages, setMessages] = useState([]);

  /* Chat input */
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);

  /* Sidebar search */
  const [searchTerm, setSearchTerm] = useState("");

  /* Auto-scroll anchor */
  const bottomRef = useRef(null);

  /* ── Step 1: Sign into Firebase ── */
  useEffect(() => {
    if (!token || !uid) return;
    setFbError("");
    ensureFirebaseLoginWeb(token, uid)
      .then(() => setFbReady(true))
      .catch((err) =>
        setFbError("Chat connection failed: " + (err?.message || String(err))),
      );
  }, [token, uid]);

  /* ── Step 2: Listen to conversations ── */
  useEffect(() => {
    if (!fbReady || !uid) return;

    const field = isOwner ? "ownerId" : "renterId";
    const deletedField = isOwner ? "deletedForOwner" : "deletedForRenter";

    const q = fsQuery(
      collection(db, "conversations"),
      where(field, "==", uid),
      where(deletedField, "==", false),
      orderBy("updatedAt", "desc"),
    );

    setConvoLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const convos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setConversations(convos);
        setConvoLoading(false);
        setConvoError("");
        // Auto-select the first conversation if nothing is selected
        setSelectedId(
          (prev) => prev || (convos.length > 0 ? convos[0].id : null),
        );
      },
      (err) => {
        console.error("[InboxPage] conversations listener error:", err);
        setConvoError("Failed to load conversations.");
        setConvoLoading(false);
      },
    );

    return unsub;
  }, [fbReady, uid, isOwner]);

  /* ── Step 3: Listen to messages for the selected conversation ── */
  useEffect(() => {
    if (!fbReady || !selectedId) {
      setMessages([]);
      return;
    }

    const q = fsQuery(
      collection(db, "conversations", selectedId, "messages"),
      orderBy("createdAt", "asc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, [fbReady, selectedId]);

  /* Auto-scroll when messages change */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ── Filtered conversation list ── */
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((c) =>
      `${c.listingTitle || ""} ${c.renterName || ""} ${c.ownerName || ""} ${c.lastMessage || ""}`
        .toLowerCase()
        .includes(term),
    );
  }, [conversations, searchTerm]);

  const selectedConvo = conversations.find((c) => c.id === selectedId) || null;

  /* ── Send message ── */
  const sendMessage = async () => {
    const text = chatInput.trim();
    if (!text || !selectedId || sending) return;
    setSending(true);
    try {
      await addDoc(collection(db, "conversations", selectedId, "messages"), {
        text,
        senderId: uid,
        senderName: user?.name || "Anonymous",
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "conversations", selectedId), {
        lastMessage: text,
        updatedAt: serverTimestamp(),
        deletedForOwner: false,
        deletedForRenter: false,
      });
      setChatInput("");
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSending(false);
    }
  };

  /* ── Soft-delete conversation for the current user ── */
  const deleteConversation = async (convId) => {
    const field = isOwner ? "deletedForOwner" : "deletedForRenter";
    try {
      await updateDoc(doc(db, "conversations", convId), { [field]: true });
      if (selectedId === convId) setSelectedId(null);
    } catch (err) {
      console.error("Failed to delete conversation:", err);
    }
  };

  /* ── Peer label (the "other side" of the conversation) ── */
  const peerLabel = (convo) => {
    if (!convo) return "";
    return isOwner ? convo.renterName || "Renter" : convo.ownerName || "Owner";
  };

  /* ── Render ── */
  if (fbError) {
    return (
      <main className="app-surface app-section-spacer">
        <section className="content-card inbox-shell">
          <p style={{ color: "#dc2626", textAlign: "center", padding: "2rem" }}>
            {fbError}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="app-surface app-section-spacer">
      <section className="content-card inbox-shell">
        <div className="inbox-header-row">
          <div>
            <p className="eyebrow">Inbox</p>
            <h1>Conversations</h1>
          </div>
          <input
            className="inbox-search"
            placeholder="Search messages"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {!fbReady ? (
          <p
            className="soft-note"
            style={{ textAlign: "center", padding: "2rem" }}
          >
            Connecting to chat…
          </p>
        ) : (
          <div className="inbox-grid">
            {/* ── Conversation sidebar ── */}
            <aside className="inbox-list" aria-label="Conversation list">
              {convoLoading ? (
                <p className="soft-note" style={{ padding: "1rem" }}>
                  Loading…
                </p>
              ) : convoError ? (
                <p
                  className="soft-note"
                  style={{ padding: "1rem", color: "#dc2626" }}
                >
                  {convoError}
                </p>
              ) : filtered.length === 0 ? (
                <p className="soft-note" style={{ padding: "1rem" }}>
                  {conversations.length === 0
                    ? "No conversations yet."
                    : "No matches for your search."}
                </p>
              ) : (
                filtered.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={
                      item.id === selectedId
                        ? "conversation-item active"
                        : "conversation-item"
                    }
                    onClick={() => setSelectedId(item.id)}
                  >
                    <div className="conversation-row">
                      <strong>{item.listingTitle || "Property"}</strong>
                      <span className="conversation-meta">
                        <span>{fmtTime(item.updatedAt)}</span>
                      </span>
                    </div>
                    <p>{peerLabel(item)}</p>
                    {item.lastMessage ? (
                      <p className="soft-note">{item.lastMessage}</p>
                    ) : null}
                  </button>
                ))
              )}
            </aside>

            {/* ── Message pane ── */}
            <section className="message-pane" aria-live="polite">
              {selectedConvo ? (
                <>
                  <div className="chat-header">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "0.5rem",
                      }}
                    >
                      <div>
                        <h2 className="chat-title">
                          {selectedConvo.listingTitle || "Property"}
                        </h2>
                        <p className="soft-note chat-subtitle">
                          {peerLabel(selectedConvo)}
                        </p>
                      </div>
                      <button
                        type="button"
                        style={{
                          background: "none",
                          border: "1px solid #fca5a5",
                          color: "#dc2626",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: "0.78rem",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        onClick={() => {
                          if (
                            window.confirm(
                              "Remove this conversation from your inbox?",
                            )
                          ) {
                            deleteConversation(selectedConvo.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="chat-body">
                    {messages.length === 0 ? (
                      <p
                        className="soft-note"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        No messages yet. Say hello!
                      </p>
                    ) : (
                      messages.map((msg) => {
                        const isMine = msg.senderId === uid;
                        return (
                          <div
                            key={msg.id}
                            className={`message-bubble ${isMine ? "outgoing" : "incoming"}`}
                          >
                            <span className="bubble-sender">
                              {msg.senderName}
                            </span>
                            <p className="bubble-text">{msg.text}</p>
                          </div>
                        );
                      })
                    )}
                    <div ref={bottomRef} />
                  </div>

                  <div className="chat-input-area">
                    <input
                      className="chat-input-field"
                      placeholder="Type a message…"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="chat-send-btn"
                      onClick={sendMessage}
                      disabled={!chatInput.trim() || sending}
                    >
                      {sending ? "…" : "Send"}
                    </button>
                  </div>
                </>
              ) : (
                <p
                  className="soft-note"
                  style={{ textAlign: "center", padding: "2rem" }}
                >
                  Select a conversation to view messages.
                </p>
              )}
            </section>
          </div>
        )}
      </section>
    </main>
  );
}
